import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import { brl } from "@/lib/format";
import { paraCentavos, paraCampo, paraMicros } from "@/lib/dinheiro";
import { PageHeader, Panel, Campo, Escolha } from "@/components/parker";
import {
  useEmpresas,
  useCategorias,
  useCentros,
  useContas,
  useContrapartes,
  useProjetos,
  useAlcadas,
} from "@/lib/dados";
import { criarLancamento, classificarMensagem } from "@/lib/fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/lancamentos/novo")({ component: Novo });

const hoje = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });

const FORM_INICIAL = {
  descricao: "",
  tipo: "despesa",
  valor: "",
  moeda: "BRL",
  taxaCambio: "1",
  dataCompetencia: hoje(),
  dataCaixa: hoje(),
  empresaPagadoraId: "",
  empresaBeneficiadaId: "",
  contaBancariaId: "",
  categoriaId: "",
  centroCustoId: "",
  contraparteId: "",
  projetoId: "",
  observacao: "",
};

function Novo() {
  const navigate = useNavigate();
  const empresas = useEmpresas();
  const categorias = useCategorias();
  const centros = useCentros();
  const contas = useContas();
  const contrapartes = useContrapartes();
  const projetos = useProjetos();
  const alcadas = useAlcadas();

  const [f, setF] = useState(FORM_INICIAL);
  const [mensagem, setMensagem] = useState("");
  const [justificativa, setJustificativa] = useState("");
  const [classificando, setClassificando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  // Marca que a mensagem livre originou o lançamento — vira a origem no banco.
  const [veioDeMensagem, setVeioDeMensagem] = useState(false);

  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));

  const valorCentavos = paraCentavos(f.valor);
  const categoriasDoTipo = categorias.dados.filter((c) => c.tipo === f.tipo);

  // A alçada não é digitada: ela decorre do valor. O painel lateral mostra em
  // tempo real quantas assinaturas este valor vai exigir.
  const aprovadores = useMemo(() => {
    const faixa = alcadas.dados
      .filter(
        (a) =>
          Math.abs(valorCentavos) > a.valorMinCentavos &&
          (a.valorMaxCentavos === null || Math.abs(valorCentavos) <= a.valorMaxCentavos),
      )
      .sort((a, b) => b.valorMinCentavos - a.valorMinCentavos)[0];
    return faixa?.aprovadoresNecessarios ?? 1;
  }, [alcadas.dados, valorCentavos]);

  const geraIntercompany =
    f.tipo === "despesa" &&
    f.empresaPagadoraId !== "" &&
    f.empresaBeneficiadaId !== "" &&
    f.empresaPagadoraId !== f.empresaBeneficiadaId;

  async function classificar() {
    setClassificando(true);
    try {
      const r = await classificarMensagem({ texto: mensagem });
      const holding = empresas.dados.find((e) => e.tipo === "holding");
      const contaDaHolding = contas.dados.find((c) => c.empresaId === holding?.id);

      setF((s) => ({
        ...s,
        descricao: r.descricao || s.descricao,
        tipo: r.tipo,
        valor: r.valorCentavos ? paraCampo(r.valorCentavos) : s.valor,
        dataCompetencia: r.dataCompetencia ?? s.dataCompetencia,
        // Quem paga é quase sempre a holding, que concentra a conta.
        empresaPagadoraId: holding?.id ?? s.empresaPagadoraId,
        contaBancariaId: contaDaHolding?.id ?? s.contaBancariaId,
        empresaBeneficiadaId: r.empresaBeneficiadaId ?? "",
        categoriaId: r.categoriaId ?? "",
        centroCustoId: r.centroCustoId ?? "",
        projetoId: r.projetoId ?? "",
        contraparteId: r.contraparteId ?? "",
        observacao: `Mensagem original: ${mensagem}`,
      }));
      setJustificativa(r.justificativa);
      setVeioDeMensagem(true);
      toast.success("Classificado — revise antes de enviar.");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setClassificando(false);
    }
  }

  async function salvar(enviarParaAprovacao: boolean) {
    setSalvando(true);
    try {
      const r = await criarLancamento({
        descricao: f.descricao,
        tipo: f.tipo,
        valorCentavos,
        moeda: f.moeda,
        taxaCambioMicros: paraMicros(f.taxaCambio),
        dataCompetencia: f.dataCompetencia,
        dataCaixa: f.dataCaixa || null,
        empresaPagadoraId: f.empresaPagadoraId || null,
        empresaBeneficiadaId: f.empresaBeneficiadaId,
        contaBancariaId: f.contaBancariaId || null,
        categoriaId: f.categoriaId,
        centroCustoId: f.centroCustoId,
        projetoId: f.projetoId || null,
        contraparteId: f.contraparteId || null,
        observacao: f.observacao || null,
        origem: veioDeMensagem ? "mensagem" : "formulario",
        enviarParaAprovacao,
      });

      if (r.possivelDuplicidade) {
        toast.warning("Possível duplicidade: mesmo fornecedor e valor em datas próximas.");
      }
      toast.success(
        enviarParaAprovacao
          ? `Enviado para aprovação — ${r.aprovacoesNecessarias} assinatura(s) necessária(s).`
          : "Rascunho salvo.",
      );
      void navigate({ to: "/lancamentos" });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Novo lançamento"
        subtitle="A alçada é aplicada automaticamente pelo valor — não é digitada."
      />

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <Panel className="border-emerald/30 p-5">
            <div className="label-caps flex items-center gap-1.5 text-emerald">
              <Sparkles className="size-3.5" /> Lançamento rápido por mensagem
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Escreva como mandaria no WhatsApp. O Parker sugere empresa, plano de contas e projeto
              — você confere antes de enviar.
            </p>
            <Textarea
              rows={2}
              className="mt-3"
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              placeholder="Ex.: paguei R$ 4.800 ao escritório de advocacia pela constituição da nova empresa, ref. setembro"
            />
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button
                onClick={() => void classificar()}
                disabled={classificando || mensagem.trim().length < 3}
              >
                {classificando ? (
                  <>
                    <Loader2 className="size-4 animate-spin" /> Classificando…
                  </>
                ) : (
                  "Classificar"
                )}
              </Button>
              {justificativa ? (
                <span className="text-xs text-muted-foreground">{justificativa}</span>
              ) : null}
            </div>
          </Panel>

          <Panel className="p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo label="Descrição" className="sm:col-span-2">
                <Input value={f.descricao} onChange={(e) => set("descricao", e.target.value)} />
              </Campo>

              <Campo label="Tipo">
                <Escolha
                  value={f.tipo}
                  onChange={(v) => {
                    set("tipo", v);
                    set("categoriaId", "");
                  }}
                  options={[
                    { id: "receita", label: "Receita" },
                    { id: "despesa", label: "Despesa" },
                    { id: "nao_operacional", label: "Não operacional" },
                  ]}
                />
              </Campo>

              <Campo label="Valor (R$)">
                <Input
                  inputMode="decimal"
                  className="num"
                  placeholder="0,00"
                  value={f.valor}
                  onChange={(e) => set("valor", e.target.value)}
                />
              </Campo>

              <Campo label="Moeda">
                <Escolha
                  value={f.moeda}
                  onChange={(v) => set("moeda", v)}
                  options={[
                    { id: "BRL", label: "BRL" },
                    { id: "USD", label: "USD" },
                    { id: "EUR", label: "EUR" },
                  ]}
                />
              </Campo>

              <Campo label="Taxa de câmbio usada" hint="1 quando o lançamento já está em reais.">
                <Input
                  className="num"
                  value={f.taxaCambio}
                  onChange={(e) => set("taxaCambio", e.target.value)}
                />
              </Campo>

              <Campo label="Data de competência" hint="Quando o fato aconteceu.">
                <Input
                  type="date"
                  value={f.dataCompetencia}
                  onChange={(e) => set("dataCompetencia", e.target.value)}
                />
              </Campo>

              <Campo label="Data de caixa" hint="Quando o dinheiro se move.">
                <Input
                  type="date"
                  value={f.dataCaixa}
                  onChange={(e) => set("dataCaixa", e.target.value)}
                />
              </Campo>

              <Campo label="Empresa que paga / recebe">
                <Escolha
                  value={f.empresaPagadoraId}
                  onChange={(v) => set("empresaPagadoraId", v)}
                  options={empresas.dados.map((e) => ({ id: e.id, label: e.nome }))}
                />
              </Campo>

              <Campo label="Empresa beneficiada" hint="De quem é o resultado.">
                <Escolha
                  value={f.empresaBeneficiadaId}
                  onChange={(v) => set("empresaBeneficiadaId", v)}
                  options={empresas.dados.map((e) => ({ id: e.id, label: e.nome }))}
                />
              </Campo>

              <Campo label="Conta bancária">
                <Escolha
                  value={f.contaBancariaId}
                  onChange={(v) => set("contaBancariaId", v)}
                  permitirVazio
                  placeholder="Sem conta"
                  options={contas.dados.map((c) => ({ id: c.id, label: c.nome }))}
                />
              </Campo>

              <Campo label="Categoria">
                <Escolha
                  value={f.categoriaId}
                  onChange={(v) => set("categoriaId", v)}
                  options={categoriasDoTipo.map((c) => ({
                    id: c.id,
                    label: `${c.codigo} · ${c.nome}`,
                  }))}
                />
              </Campo>

              <Campo label="Centro de custo">
                <Escolha
                  value={f.centroCustoId}
                  onChange={(v) => set("centroCustoId", v)}
                  options={centros.dados.map((c) => ({
                    id: c.id,
                    label: `${c.codigo} · ${c.nome}`,
                  }))}
                />
              </Campo>

              <Campo label="Fornecedor / cliente">
                <Escolha
                  value={f.contraparteId}
                  onChange={(v) => set("contraparteId", v)}
                  permitirVazio
                  placeholder="Sem contraparte"
                  options={contrapartes.dados.map((c) => ({ id: c.id, label: c.nome }))}
                />
              </Campo>

              <Campo label="Projeto">
                <Escolha
                  value={f.projetoId}
                  onChange={(v) => set("projetoId", v)}
                  permitirVazio
                  placeholder="Sem projeto"
                  options={projetos.dados
                    .filter((p) => p.ativo)
                    .map((p) => ({ id: p.id, label: p.nome }))}
                />
              </Campo>

              <Campo label="Observação" className="sm:col-span-2">
                <Textarea
                  rows={3}
                  value={f.observacao}
                  onChange={(e) => set("observacao", e.target.value)}
                />
              </Campo>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <Button onClick={() => void salvar(true)} disabled={salvando}>
                Enviar para aprovação
              </Button>
              <Button variant="outline" onClick={() => void salvar(false)} disabled={salvando}>
                Salvar rascunho
              </Button>
            </div>
          </Panel>
        </div>

        <div className="space-y-4">
          <Panel className="p-5">
            <div className="label-caps">Alçada aplicada</div>
            <div className="num mt-2 text-2xl text-emerald">{aprovadores}</div>
            <p className="mt-1 text-sm text-muted-foreground">
              aprovações necessárias para {brl(valorCentavos)}.
            </p>
            <ul className="mt-4 space-y-1 text-xs text-muted-foreground">
              {alcadas.dados.map((a) => (
                <li key={a.id} className="num">
                  {brl(a.valorMinCentavos)} —{" "}
                  {a.valorMaxCentavos === null ? "acima" : brl(a.valorMaxCentavos)}:{" "}
                  {a.aprovadoresNecessarios}
                </li>
              ))}
            </ul>
          </Panel>

          {geraIntercompany ? (
            <Panel className="border-emerald/30 p-5">
              <div className="label-caps text-emerald">Intercompany automático</div>
              <p className="mt-2 text-sm text-muted-foreground">
                O caixa sai de{" "}
                <strong className="text-foreground">
                  {empresas.dados.find((e) => e.id === f.empresaPagadoraId)?.nome}
                </strong>{" "}
                e o resultado é reconhecido em{" "}
                <strong className="text-foreground">
                  {empresas.dados.find((e) => e.id === f.empresaBeneficiadaId)?.nome}
                </strong>
                . Na aprovação, a obrigação de {brl(valorCentavos)} é criada sozinha.
              </p>
            </Panel>
          ) : null}
        </div>
      </div>
    </>
  );
}
